import { useCallback } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { type ResultOf, type VariablesOf } from "gql.tada";
import { GET_REFERENCE_DATA } from "../graphql/queries";
import { CREATE_CALCULATION } from "../graphql/mutations";

export type DimensionTypeOption = ResultOf<
  typeof GET_REFERENCE_DATA
>["dimensionTypes"][number];

export type SupplierOption = ResultOf<
  typeof GET_REFERENCE_DATA
>["suppliers"][number];

export type SubCategoryOption = ResultOf<
  typeof GET_REFERENCE_DATA
>["calculationCategoryDefinitions"][number]["subCategories"][number];

/** A category paired with its calculable subcategories, for the Step 2 dropdowns. */
export interface CalculableCategory {
  id: string;
  name: string;
  subCategories: SubCategoryOption[];
}

type CreateCalculationInput = VariablesOf<typeof CREATE_CALCULATION>["input"];

interface UseCreateCalculationPanelResult {
  /** Categories with >=1 calculable subcategory, each carrying its calculable subs, in display order. */
  calculableCategories: CalculableCategory[];
  /** Flat list of all calculable subcategories, for name/dimension lookup by id. */
  subCategoryOptions: SubCategoryOption[];
  dimensionTypeOptions: DimensionTypeOption[];
  supplierOptions: SupplierOption[];
  refDataLoading: boolean;
  createCalculation: (input: CreateCalculationInput) => void;
  createLoading: boolean;
}

export interface UseCreateCalculationPanelOptions {
  onSuccess?: (id: string) => void;
  onClose?: () => void;
  onError?: (message: string) => void;
}

// errorPolicy: 'all' fires onCompleted with data: null on GraphQL errors —
// the null branch is the error path, not a defensive check.
export const buildCreateCalculationHandlers = (
  options: UseCreateCalculationPanelOptions,
) => ({
  onCompleted: (data: { createCalculation: { id: string } | null } | null) => {
    if (!data?.createCalculation) {
      options.onError?.("Server returned no calculation");
      return;
    }
    options.onClose?.();
    options.onSuccess?.(data.createCalculation.id);
  },
  onError: (error: { message: string }) => {
    options.onError?.(error.message);
  },
});

const useCreateCalculationPanel = (
  active: boolean,
  options: UseCreateCalculationPanelOptions = {},
): UseCreateCalculationPanelResult => {
  const { onSuccess, onClose, onError } = options;

  const { data: refData, loading: refDataLoading } = useQuery(
    GET_REFERENCE_DATA,
    { skip: !active },
  );

  const [runCreateCalculation, { loading: createLoading }] = useMutation(
    CREATE_CALCULATION,
    buildCreateCalculationHandlers({ onSuccess, onClose, onError }),
  );

  // A subcategory is "calculable" only if it has a default formula template;
  // formula-less placeholders (Personnel, Overhead, …) would create non-computing
  // calcs. Categories left with no calculable subcategory drop out entirely.
  const calculableCategories: CalculableCategory[] = [
    ...(refData?.calculationCategoryDefinitions ?? []),
  ]
    .map((cat) => ({
      id: cat.id,
      name: cat.name,
      displayOrder: cat.displayOrder,
      subCategories: [...cat.subCategories]
        .filter((sub) => sub.defaultFormulaTemplateId != null)
        .sort((a, b) => a.displayOrder - b.displayOrder),
    }))
    .filter((cat) => cat.subCategories.length > 0)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map(({ id, name, subCategories }) => ({ id, name, subCategories }));

  const subCategoryOptions: SubCategoryOption[] = calculableCategories.flatMap(
    (cat) => cat.subCategories,
  );

  const dimensionTypeOptions: DimensionTypeOption[] = (
    refData?.dimensionTypes ?? []
  ).map((type) => ({
    ...type,
    values: [...type.values].sort((a, b) => a.localeCompare(b)),
  }));

  const supplierOptions: SupplierOption[] = [
    ...(refData?.suppliers ?? []),
  ].sort((a, b) => a.name.localeCompare(b.name));

  const createCalculation = useCallback(
    (input: CreateCalculationInput) => {
      runCreateCalculation({ variables: { input } });
    },
    [runCreateCalculation],
  );

  return {
    calculableCategories,
    subCategoryOptions,
    dimensionTypeOptions,
    supplierOptions,
    refDataLoading,
    createCalculation,
    createLoading,
  };
};

export default useCreateCalculationPanel;
